/**
 * Registry bridge — the module-singleton that carries the plugin registry (and
 * the live host ctx) to the workbench surfaces mounted OUTSIDE the plugin host.
 *
 * `WorkbenchPluginHost` provides the registry via React context, but two menu
 * surfaces live ABOVE the editor route where the host mounts: Studio's brand/app
 * menu (in the top bar) and the settings modal (in the workspace layout). They
 * can't read the context, so a `<RegistryBridge/>` mounted INSIDE the composition
 * (below both the plugin host and the host-ctx provider — so it reads the real
 * registry AND the live ctx) publishes them here on mount and clears them on
 * unmount. This generalizes the bespoke `exportSectionStore` (which bridged only
 * the export-share menu section) into ONE registry bridge the `menuItems` and
 * `settingsPages` seams share.
 *
 * A second, DYNAMIC menu-items channel exists because Studio's export/share items
 * are board-coupled (they close over the live board ref, active tab kind, and the
 * process-image export) — they can't be static plugin data. The board composition
 * (in-host) publishes them here as `MenuItemContribution`s; the brand menu merges
 * them with the static registry menu items. Both flow through the same shape and
 * the same `{when,run}` resolver.
 */
import { useEffect, useSyncExternalStore } from "react";
import {
  isActionable,
  useWorkbenchHost,
  type WorkbenchHostContext,
} from "./hostContext.js";
import { usePluginRegistry, type Owned, type PluginRegistry } from "./PluginRegistryContext.js";
import type { MenuItemContribution, SettingsPageContribution } from "./types.js";

/** The current publication: the plugin host's built registry plus the live ctx
 *  to resolve `when`/`run` against. Null when no host is mounted (the viewer, or
 *  before an editor route mounts) — out-of-host surfaces then read empty lists. */
export interface RegistryPublication {
  readonly registry: PluginRegistry;
  readonly ctx: WorkbenchHostContext;
}

let _pub: RegistryPublication | null = null;
let _dynamic: readonly MenuItemContribution[] = Object.freeze([]);
const _subs = new Set<() => void>();

function emit(): void {
  _subs.forEach((fn) => fn());
}
function subscribe(fn: () => void): () => void {
  _subs.add(fn);
  return () => _subs.delete(fn);
}

/** Publish the built registry + live ctx (called by {@link RegistryBridge}).
 *  Pass `null` on unmount to clear. */
export function publishRegistry(pub: RegistryPublication | null): void {
  if (_pub === pub) return;
  _pub = pub;
  emit();
}

/** Mount ONCE inside the workbench composition, below the plugin host AND the
 *  host-ctx provider, so it reads the real registry + the live ctx and mirrors
 *  them to the bridge for the out-of-host menu surfaces. Renders nothing. */
export function RegistryBridge(): null {
  const registry = usePluginRegistry();
  const ctx = useWorkbenchHost();
  useEffect(() => {
    publishRegistry({ registry, ctx });
    return () => publishRegistry(null);
  }, [registry, ctx]);
  return null;
}

/** Called by the in-host board composition to publish board-coupled export/share
 *  menu items (the migrated `exportSectionStore` payload). Replace the whole set;
 *  pass `[]` on unmount. */
export function publishDynamicMenuItems(items: readonly MenuItemContribution[]): void {
  if (_dynamic === items) return;
  _dynamic = Object.freeze([...items]);
  emit();
}

function getPub(): RegistryPublication | null {
  return _pub;
}
function getDynamic(): readonly MenuItemContribution[] {
  return _dynamic;
}

/** Subscribe to the current publication (registry + ctx), or null. */
export function useRegistryPublication(): RegistryPublication | null {
  return useSyncExternalStore(subscribe, getPub, getPub);
}

function useDynamicMenuItems(): readonly MenuItemContribution[] {
  return useSyncExternalStore(subscribe, getDynamic, getDynamic);
}

/** A menu item resolved against the live ctx, ready to render + run. */
export interface ResolvedMenuItem {
  readonly id: string;
  readonly group?: string;
  readonly label: string;
  readonly icon?: MenuItemContribution["icon"];
  readonly danger?: boolean;
  readonly disabled?: boolean;
  run(): void;
}

/** The app-menu items visible right now — static registry items merged with the
 *  in-host dynamic (export) items, all `when`-gated against the live ctx and
 *  sorted by order. Empty when no host is mounted. For Studio's BrandMenu. */
export function useOutOfHostMenuItems(): readonly ResolvedMenuItem[] {
  const pub = useRegistryPublication();
  const dynamic = useDynamicMenuItems();
  if (!pub) return EMPTY_MENU;
  const { ctx } = pub;
  const staticItems = pub.registry.menuItems.map((o) => o.contribution);
  const all = [...staticItems, ...dynamic]
    .filter((c) => isActionable(c, ctx))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return all.map((c) => ({
    id: c.id,
    group: c.group,
    label: c.label,
    icon: c.icon,
    danger: c.danger,
    disabled: c.disabled,
    run: () => c.run(ctx),
  }));
}

/** The settings pages visible right now, `when`-gated against the live ctx and
 *  sorted by order, tagged with their owning plugin id (for the error boundary).
 *  Empty when no host is mounted. For Studio's SettingsModal. */
export interface ResolvedSettingsPage {
  readonly pluginId: string;
  readonly contribution: SettingsPageContribution;
}
export function useOutOfHostSettingsPages(): readonly ResolvedSettingsPage[] {
  const pub = useRegistryPublication();
  if (!pub) return EMPTY_PAGES;
  const { ctx } = pub;
  return pub.registry.settingsPages
    .filter((o: Owned<SettingsPageContribution>) => isActionable(o.contribution, ctx))
    .slice()
    .sort((a, b) => (a.contribution.order ?? 0) - (b.contribution.order ?? 0))
    .map((o) => ({ pluginId: o.pluginId, contribution: o.contribution }));
}

/** The live host ctx, or the registry's empty ctx when no host is mounted — so a
 *  contributed settings Page always has a callable ctx. */
export function useOutOfHostCtx(): WorkbenchHostContext | null {
  return useRegistryPublication()?.ctx ?? null;
}

const EMPTY_MENU: readonly ResolvedMenuItem[] = Object.freeze([]);
const EMPTY_PAGES: readonly ResolvedSettingsPage[] = Object.freeze([]);
